import { useAppSelector } from '@/store/hooks';
import {
  type ClientRecapFormat,
  type ClientRecapLength,
  type ClientRecapTone,
  postClientRecap,
} from '@/store/slices/reportingV2/reportingV2.api';
import {
  selectReportingV2Filters,
  selectReportingV2Freshness,
  selectReportingV2Widget,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { AutoAwesome, ContentCopyRounded } from '@mui/icons-material';
import { Box, Button, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { channelLabel, isFrequencyComparable } from '../format';
import { effectiveEndDate, usePriorPeriodTotals } from '../hooks/usePriorPeriod';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { COLORS, cardSx } from '../theme';

const TONE_OPTIONS: ClientRecapTone[] = ['Professional', 'Casual', 'Enthusiastic'];
const LENGTH_OPTIONS: ClientRecapLength[] = ['Short', 'Medium', 'Long'];
const FORMAT_OPTIONS: ClientRecapFormat[] = ['Email', 'Bullet Points', 'Paragraph'];

const pillSelectSx = {
  fontSize: '0.8rem',
  color: COLORS.muted,
  bgcolor: COLORS.pageBg,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-select': { py: 0.6, px: 1.25 },
} as const;

const num = (value: unknown): number | undefined => {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * flank#4625 sweep — the recap used to send wholesale Spend into the
 * client-facing narrative unconditionally. Falls back to Client Spend the
 * same way TopCampaignsCard/TopPublishersCard choose their ranking metric
 * (#4625), and omits the figure entirely rather than send either when the
 * viewer has neither permission.
 */
export const recapSpend = (
  row: { spend?: unknown; client_spend?: unknown },
  canViewWholesaleSpend: boolean,
  canViewClientSpend: boolean,
): number | undefined => {
  if (canViewWholesaleSpend) return num(row.spend);
  if (canViewClientSpend) return num(row.client_spend);
  return undefined;
};

const ClientRecapCard = () => {
  const filters = useAppSelector(selectReportingV2Filters);
  const { rows, loading, error } = useAppSelector(selectReportingV2Widget('totals'));
  const prior = usePriorPeriodTotals();
  const { rows: channelRows } = useAppSelector(selectReportingV2Widget('channel'));
  const freshness = useAppSelector(selectReportingV2Freshness);
  const { canViewWholesaleSpend, canViewClientSpend, canViewPlatform } = useReportingPermissions();

  const [tone, setTone] = useState<ClientRecapTone>('Professional');
  const [length, setLength] = useState<ClientRecapLength>('Medium');
  const [format, setFormat] = useState<ClientRecapFormat>('Email');
  const [narrative, setNarrative] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const totalsRow = rows[0] ?? {};
  const hasData = Number(totalsRow.impressions ?? 0) > 0;

  // Generation is a manual, paid-LLM-backed action (the Generate button), so
  // a filter/date-range change can't auto-fire a new request — but leaving
  // the old narrative on screen would describe a period/scope that no longer
  // matches the rest of the page. Clear it back to the pre-generation prompt
  // instead, requiring an explicit Regenerate.
  useEffect(() => {
    setNarrative(null);
    setGenError(null);
    setCopied(false);
    setCopyError(false);
  }, [filters]);

  // The freshness line elsewhere on this page ("reported through N days ago")
  // already tells the reader the requested window and the actual data don't
  // match when a platform lags — the recap's date range has to agree with it,
  // or the narrative describes data that isn't there yet (flank#4572). Capped
  // to whichever in-scope platform is most stale, never later than the
  // filter's own end date.
  const rawEffectiveDateEnd = effectiveEndDate(filters.dateEnd, filters.dsps, freshness);
  // A short window (e.g. "Last 7 Days") can have less slack than the most
  // stale in-scope platform's lag, so the clamp above can land before
  // dateStart — sending the backend an inverted range it correctly 422s on.
  // Floor it instead and treat that case as "nothing fresh in this window"
  // rather than firing a request we already know is invalid.
  const hasFreshDataForRange = rawEffectiveDateEnd >= filters.dateStart;
  const effectiveDateEnd = hasFreshDataForRange ? rawEffectiveDateEnd : filters.dateStart;

  const generate = async () => {
    setGenerating(true);
    setGenError(null);
    setCopied(false);
    setCopyError(false);
    try {
      const priorRow = prior.rows[0];
      const { narrative: text } = await postClientRecap({
        dateStart: filters.dateStart,
        dateEnd: effectiveDateEnd,
        tone,
        length,
        format,
        totals: {
          impressions: num(totalsRow.impressions),
          spend: recapSpend(totalsRow, canViewWholesaleSpend, canViewClientSpend),
          clicks: num(totalsRow.clicks),
          ctr: num(totalsRow.ctr),
          // Reach summed across platforms double-counts anyone reached on more
          // than one — same reason the KPI strip withholds it outside a
          // single-platform filter (isFrequencyComparable). The recap used to
          // send it unconditionally, so the narrative stated a real-looking
          // number the page itself showed as "—" (flank#4572).
          reach: isFrequencyComparable(filters.dsps) ? num(totalsRow.reach) : undefined,
          vcr: num(totalsRow.vcr),
        },
        priorTotals: priorRow
          ? {
              impressions: num(priorRow.impressions),
              spend: recapSpend(priorRow, canViewWholesaleSpend, canViewClientSpend),
              clicks: num(priorRow.clicks),
            }
          : undefined,
        channelBreakdown: channelRows
          .filter(r => r.channel != null)
          .slice(0, 8)
          .map(r => ({
            // Labelled, not the raw value — this prompt is the one surface
            // AI recap text reaches, so an unlabeled/un-masked channel here
            // leaked both a whitelabel rename and the GAM-prefix mask
            // straight into client-facing copy (flank#4469).
            channel: channelLabel(r.channel, canViewPlatform),
            impressions: num(r.impressions) ?? 0,
            spend: recapSpend(r, canViewWholesaleSpend, canViewClientSpend),
          })),
      });
      setNarrative(text);
    } catch {
      setGenError('Recap generation failed. Try again in a moment.');
    } finally {
      setGenerating(false);
    }
  };

  const copy = async () => {
    if (!narrative) return;
    try {
      await navigator.clipboard.writeText(narrative);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyError(true);
      setTimeout(() => setCopyError(false), 3000);
    }
  };

  return (
    <Box sx={{ ...cardSx, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome sx={{ fontSize: 18, color: COLORS.primary }} />
          <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
            Client recap
          </Typography>
          <Typography variant='body2' sx={{ color: COLORS.muted }}>
            {loading
              ? '— preparing…'
              : error
                ? '— unavailable'
                : !hasFreshDataForRange
                  ? '— not enough fresh data for this range'
                  : !hasData
                    ? '— no data'
                    : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select
            size='small'
            value={tone}
            onChange={e => setTone(e.target.value as ClientRecapTone)}
            sx={pillSelectSx}
          >
            {TONE_OPTIONS.map(t => (
              <MenuItem key={t} value={t}>
                Tone: {t}
              </MenuItem>
            ))}
          </Select>
          <Select
            size='small'
            value={length}
            onChange={e => setLength(e.target.value as ClientRecapLength)}
            sx={pillSelectSx}
          >
            {LENGTH_OPTIONS.map(l => (
              <MenuItem key={l} value={l}>
                Length: {l}
              </MenuItem>
            ))}
          </Select>
          <Select
            size='small'
            value={format}
            onChange={e => setFormat(e.target.value as ClientRecapFormat)}
            sx={pillSelectSx}
          >
            {FORMAT_OPTIONS.map(f => (
              <MenuItem key={f} value={f}>
                Format: {f}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant='outlined'
            disableElevation
            disabled={!hasData || !hasFreshDataForRange || generating}
            onClick={generate}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            {generating ? 'Generating…' : narrative ? 'Regenerate' : 'Generate'}
          </Button>
          <Tooltip title={narrative ? 'Copy recap' : 'Generate a recap first'} placement='top' arrow>
            <span>
              <Button
                variant='contained'
                disableElevation
                disabled={!narrative}
                onClick={copy}
                startIcon={<ContentCopyRounded sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  bgcolor: COLORS.primary,
                  color: '#fff',
                  borderRadius: '8px',
                  '&:hover': { bgcolor: COLORS.primaryHover },
                }}
              >
                {copied ? 'Copied' : copyError ? 'Copy failed' : 'Copy'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Typography
        variant='body2'
        component='pre'
        sx={{
          color: genError ? 'error.main' : COLORS.muted,
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit',
          m: 0,
        }}
      >
        {genError
          ? genError
          : narrative
            ? narrative
            : loading
              ? 'Pulling the latest numbers for the selected window…'
              : error
                ? error
                : hasData
                  ? 'Choose a tone, length and format, then Generate to draft a recap of the selected window.'
                  : 'No data in the selected window. Try broadening the date range or clearing filters.'}
      </Typography>
    </Box>
  );
};

export default ClientRecapCard;
